import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  connectionQualityQuery,
  type CqBaseline,
  type CqPage,
  type CqRange,
  type CqTraceMark,
  type CqVerdict,
} from '../api/connectionQuality'
import { DeviceFloorPosition, FloorPlansButton } from '../floorplan/DeviceFloorPosition'
import { Spinner } from '../ui/Spinner'
import { CQ_COLORS, CqChart, CqChartSync, parseTime, type CqChartMarker, type CqChartSeries } from './CqChart'
import { CqAccessPoints } from './CqAccessPoints'
import { CompareLine, CqChecksCategory } from './CqChecks'
import { CqGrid } from './CqGrid'
import { CqPath } from './CqPath'
import { CqSpeed } from './CqSpeed'
import { CqWifi } from './CqWifi'
import { windowToQuery, zoomBy, type CqZoomQuery } from './cqZoom'
import type { ScreenProps } from './screens'
import './DeviceConnectionQuality.css'

/**
 * Fila Connection Quality (Proactivitee, dupa Network Monitoring). Modul nou,
 * numai in v4: ce masoara agentul despre legatura masinii INSESI - routerul,
 * furnizorul, tinta - si concluzia scrisa despre unde e problema.
 *
 * Concluzia si "normalul aparatului" se calculeaza pe server
 * (Verdict.php, Baseline.php); ecranul doar le arata.
 *
 * Faza 3: sectiunea Wi-Fi (CqWifi.tsx), numai cand aparatul are date Wi-Fi.
 * Faza 4: sectiunea Speed (CqSpeed.tsx), deasupra traseului.
 * Verificarile (MTU, DNS, portal captiv, VPN): cate un card, sus de tot.
 *
 * Perioade: 1h si 4h (pe minut), 24h, 7 zile, 30 de zile. Peste ele, zoom:
 * tragi peste un grafic (sau +/- pe ecranele tactile) si toate graficele trec
 * pe intervalul ales, adus din nou de la server (cqZoom.ts). "Open in new
 * tab" deschide acelasi ecran pe o pagina a lui, mare, cu perioada si zoom-ul
 * in adresa (ConnectionQualityFullPage.tsx) - ca fisa CMDB.
 */

const RANGE_LABEL: Record<CqRange, string> = {
  '1h': '1 hour',
  '4h': '4 hours',
  '24h': '24 hours',
  '7d': '7 days',
  '30d': '30 days',
}

const RANGE_MS: Record<CqRange, number> = {
  '1h': 3_600_000,
  '4h': 4 * 3_600_000,
  '24h': 86_400_000,
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000,
}

export const CQ_RANGES = Object.keys(RANGE_LABEL) as CqRange[]

export const LINES = [
  { key: 'gateway', name: 'Router', color: CQ_COLORS[0] },
  { key: 'isp', name: 'Provider', color: CQ_COLORS[1] },
  { key: 'target', name: 'Target', color: CQ_COLORS[2] },
] as const

const HTTP = [
  { key: 'dns_ms', name: 'DNS', color: CQ_COLORS[0] },
  { key: 'tcp_ms', name: 'TCP connect', color: CQ_COLORS[1] },
  { key: 'tls_ms', name: 'TLS', color: CQ_COLORS[2] },
  { key: 'ttfb_ms', name: 'First byte', color: CQ_COLORS[3] },
] as const

/** Traseele ca marcaje pe graficul de timp; cele cu drum schimbat ies in fata. */
export function traceMarkers(marks: CqTraceMark[]): CqChartMarker[] {
  return marks.map((m) => ({ t: m.at, label: m.changed ? 'Path changed' : `Traceroute (${m.reason})`, strong: m.changed }))
}

const TONE: Record<CqVerdict['code'], { cls: string; icon: string }> = {
  ok: { cls: 'is-ok', icon: 'fas fa-check-circle' },
  local: { cls: 'is-bad', icon: 'fas fa-exclamation-triangle' },
  isp: { cls: 'is-bad', icon: 'fas fa-exclamation-triangle' },
  target: { cls: 'is-bad', icon: 'fas fa-exclamation-triangle' },
  no_data: { cls: 'is-none', icon: 'fas fa-info-circle' },
}

/** Ce se vede: perioada aleasa, si zoom-ul peste ea (null = toata perioada). */
export type CqViewState = { range: CqRange; zoom: CqZoomQuery | null }

export const CQ_DEFAULT_STATE: CqViewState = { range: '24h', zoom: null }

/**
 * Asezarea paginii mari: `grid` = carduri de rezumat sus si graficele mici,
 * unul langa altul, fiecare cu Expand (CqGrid.tsx); `full` = tot mare, una sub
 * alta, ca pe fila.
 */
export type CqLayout = 'grid' | 'full'

export const CQ_LAYOUTS: CqLayout[] = ['grid', 'full']

const LAYOUTS: { key: CqLayout; label: string; icon: string; title: string }[] = [
  { key: 'grid', label: 'Grid', icon: 'fas fa-th-large', title: 'Summary cards and smaller charts side by side' },
  { key: 'full', label: 'Full', icon: 'fas fa-bars', title: 'Every chart big, one under the other' },
]

/**
 * Adresa paginii mari (`/app/inventoree/device/{id}/connection-quality`), cu
 * perioada si zoom-ul de acum in ea - se poate copia si trimite.
 */
export function cqFullHref(id: number, state: CqViewState): string {
  const q = new URLSearchParams()
  if (state.range !== CQ_DEFAULT_STATE.range) q.set('range', state.range)
  if (state.zoom) {
    q.set('from', state.zoom.from)
    q.set('to', state.zoom.to)
  }
  const tail = q.toString()
  return `/app/inventoree/device/${id}/connection-quality${tail ? `?${tail}` : ''}`
}

/** Fila de pe fisa aparatului. */
export function DeviceConnectionQuality({ id, open, hasTab }: ScreenProps) {
  const [state, setState] = useState<CqViewState>(CQ_DEFAULT_STATE)
  return (
    <CqView
      id={id}
      state={state}
      onState={setState}
      onOpenSettings={() => open('overview/agentSettings/')}
      settingsTab={hasTab ? hasTab('overview/agentSettings/') : true}
    />
  )
}

/**
 * Ecranul intreg - acelasi pe fila fisei si pe pagina mare (`big`). Pagina
 * mare n-are fisa in jur, deci nici butonul "Open Agent Settings" in pagina:
 * primeste in loc o legatura spre fisa aparatului.
 */
export function CqView({
  id,
  state,
  onState,
  big = false,
  onOpenSettings,
  settingsTab = true,
  layout = 'full',
  onLayout,
}: {
  id: number
  state: CqViewState
  onState: (state: CqViewState) => void
  big?: boolean
  onOpenSettings?: () => void
  settingsTab?: boolean
  /** Numai pe pagina mare: Grid (compacta) ori Full (tot mare, una sub alta). */
  layout?: CqLayout
  /** Lipsa = fara comutator (fila fisei). */
  onLayout?: (layout: CqLayout) => void
}) {
  const { data, error, isFetching } = useQuery(connectionQualityQuery(id, state.range, state.zoom))

  if (error && !data) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const canOpenSettings = data.canEditSettings && settingsTab && !!onOpenSettings
  /* Grila numai pe pagina mare; fila fisei ramane cum era. */
  const grid = big && layout === 'grid'
  const zoomTo = (from: number, to: number) => onState({ range: state.range, zoom: windowToQuery({ from, to }) })
  const current = { from: parseTime(data.from), to: parseTime(data.to) }
  const zoomIn = () => {
    const w = zoomBy(current, 0.5, Date.now())
    zoomTo(w.from, w.to)
  }
  const zoomOut = () => {
    const w = zoomBy(current, 2, Date.now())
    /* Mai larg decat perioada aleasa: inapoi la perioada intreaga. */
    if (w.to - w.from >= RANGE_MS[state.range]) onState({ range: state.range, zoom: null })
    else zoomTo(w.from, w.to)
  }

  return (
    <div className={`cq-screen${big ? ' is-big' : ''}`}>
      <div className="cq-top">
        <h4>
          Connection Quality
          {big && data.device.name ? <span className="cq-device-name"> · {data.device.name}</span> : null}
          {isFetching ? <i className="fas fa-sync fa-spin cq-fetching" aria-label="Loading"></i> : null}
        </h4>
        <div className="cq-tools">
          <div className="btn-group cq-ranges" role="group" aria-label="Period">
            {data.ranges.map((r) => {
              const on = !state.zoom && r === state.range
              return (
                <button
                  key={r}
                  type="button"
                  className={`btn btn-sm ${on ? 'btn-primary' : 'btn-default'}`}
                  aria-pressed={on}
                  onClick={() => onState({ range: r, zoom: null })}
                >
                  {RANGE_LABEL[r] ?? r}
                </button>
              )
            })}
          </div>
          {data.settings.enabled ? (
            <div className="btn-group cq-zoom" role="group" aria-label="Zoom">
              <button type="button" className="btn btn-sm btn-default" title="Zoom in" aria-label="Zoom in" onClick={zoomIn}>
                <i className="fas fa-search-plus" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-default"
                title="Zoom out"
                aria-label="Zoom out"
                disabled={!state.zoom}
                onClick={zoomOut}
              >
                <i className="fas fa-search-minus" aria-hidden="true"></i>
              </button>
              {state.zoom ? (
                <button type="button" className="btn btn-sm btn-warning" onClick={() => onState({ range: state.range, zoom: null })}>
                  Reset zoom
                </button>
              ) : null}
            </div>
          ) : null}
          {big && onLayout && data.settings.enabled ? (
            <div className="btn-group cq-layout" role="group" aria-label="View">
              {LAYOUTS.map((l) => (
                <button
                  key={l.key}
                  type="button"
                  className={`btn btn-sm ${layout === l.key ? 'btn-primary' : 'btn-default'}`}
                  aria-pressed={layout === l.key}
                  title={l.title}
                  onClick={() => onLayout(l.key)}
                >
                  <i className={l.icon} aria-hidden="true"></i> {l.label}
                </button>
              ))}
            </div>
          ) : null}
          <FloorPlansButton id={id} />
          {big ? (
            <a className="btn btn-sm btn-default" href={`/app/inventoree/device/${id}`}>
              <i className="fas fa-desktop" aria-hidden="true"></i> Device sheet
            </a>
          ) : (
            <a
              className="btn btn-sm btn-default cq-open-full"
              href={cqFullHref(id, state)}
              target="_blank"
              rel="noopener"
              title="Everything on one page, with bigger charts. The address keeps the period and the zoom."
            >
              <i className="fas fa-external-link-alt" aria-hidden="true"></i> Open in new tab
            </a>
          )}
        </div>
      </div>
      {data.settings.enabled ? (
        <div className="cq-zoom-hint" data-zoomed={state.zoom ? 'yes' : 'no'}>
          {state.zoom ? (
            <>
              Showing <strong>{shortTime(current.from)}</strong> - <strong>{shortTime(current.to)}</strong>, {bucketLabel(data.bucket_s)}.{' '}
            </>
          ) : null}
          Drag across a chart to zoom in; hover a chart to read the exact values.
        </div>
      ) : null}

      {!data.settings.enabled ? (
        <div className="alert alert-info cq-off" role="status">
          <i className="fas fa-info-circle"></i> Connection Quality is <strong>turned off</strong> for this device
          {data.settings.from === 'company' ? ' (company default)' : data.settings.from === 'default' ? ' (nobody turned it on yet)' : ''}.
          It is turned on from the <strong>Connection Quality</strong> card in Agent Settings &rarr; Functions
          {data.settings.from !== 'device' ? (
            <>
              , or for the whole company in{' '}
              <a href="/app/settings/connection_quality" className="cq-company-link">
                Settings &rarr; Connection Quality
              </a>
            </>
          ) : null}
          .
          {canOpenSettings ? (
            <>
              {' '}
              <button type="button" className="btn btn-link btn-sm cq-link" onClick={onOpenSettings}>
                Open Agent Settings
              </button>
            </>
          ) : null}
        </div>
      ) : big ? (
        /* Pagina mare: Grid si Full au acelasi design pe categorii; Full doar
           intinde graficele, cate unul pe rand. */
        <CqGrid page={data} id={id} onZoom={zoomTo} onOpenSettings={canOpenSettings ? onOpenSettings : undefined} long={!grid} />
      ) : (
        <CqChartSync onZoom={zoomTo}>
          <Enabled page={data} big={big} onOpenSettings={canOpenSettings ? onOpenSettings : undefined} />
        </CqChartSync>
      )}
      {data.settings.enabled && !big ? <CqSpeed id={id} /> : null}
      {data.settings.enabled && !big ? <CqPath id={id} /> : null}
      {!data.settings.enabled ? <CqChecksCategory checks={data.checks} off /> : null}
      {/* Aprins, planul sta langa AP-uri (in Enabled). */}
      {!data.settings.enabled ? <DeviceFloorPosition id={id} /> : null}
    </div>
  )
}

function two(n: number): string {
  return String(n).padStart(2, '0')
}

function shortTime(ms: number): string {
  const d = new Date(ms)
  return `${two(d.getDate())}.${two(d.getMonth() + 1)} ${two(d.getHours())}:${two(d.getMinutes())}`
}

export function bucketLabel(s: number): string {
  if (s <= 60) return 'one point per minute'
  if (s < 3600) return `one point per ${Math.round(s / 60)} minutes`
  if (s === 3600) return 'one point per hour'
  return `one point per ${Math.round(s / 3600)} hours`
}

/** TCP: procentele sunt mici, deci cu doua zecimale. */
export const tcpFormat = (n: number) => String(Math.round(n * 100) / 100)

/**
 * Seriile graficelor de timp, din raspunsul serverului - aceleasi pe fila, pe
 * pagina mare si in vederea Grid a ei (CqGrid.tsx).
 */
export function cqSeries(page: CqPage) {
  const latency: CqChartSeries[] = LINES.map((l) => ({
    name: l.name,
    color: l.color,
    points: page.latency[l.key].map((p) => ({ t: p.bucket, v: p.avg, lo: p.min, hi: p.max })),
  }))
  /* Routerul care n-a raspuns la niciun ping, dar prin care trece traficul,
     ar sta la 100% pe tot graficul si ar parea o retea cazuta. */
  const loss: CqChartSeries[] = LINES.filter((l) => !(page.gatewaySilent && l.key === 'gateway')).map((l) => ({
    name: l.name,
    color: l.color,
    points: page.latency[l.key].map((p) => ({ t: p.bucket, v: p.loss_pct })),
  }))
  const mos: CqChartSeries[] = [
    { name: 'MOS', color: CQ_COLORS[2], points: page.latency.target.map((p) => ({ t: p.bucket, v: p.mos })) },
  ]
  const http: CqChartSeries[] = HTTP.map((h) => ({
    name: h.name,
    color: h.color,
    points: page.http.map((p) => ({ t: p.bucket, v: p[h.key] })),
  }))
  const tcp: CqChartSeries[] = [
    { name: 'Retransmitted', color: CQ_COLORS[0], points: page.tcp.map((p) => ({ t: p.bucket, v: p.retrans_pct })) },
  ]
  const common = { from: page.from, to: page.to, bucketS: page.bucket_s }
  /* HTTPS se masoara la 5 minute: pe galeti de un minut, punctele ar sta
     rupte unul de altul. */
  const httpCommon = { ...common, bucketS: Math.max(page.bucket_s, 300) }
  const markers = traceMarkers(page.traceMarks ?? [])
  const httpErrors = page.http.reduce((n, p) => n + p.errors, 0)
  return { latency, loss, mos, http, tcp, common, httpCommon, markers, httpErrors }
}

/** Concluzia: textul, de ce, notele, comparatia cu sediul. */
export function CqVerdictBox({ page }: { page: CqPage }) {
  const verdict = page.verdict
  const tone = TONE[verdict.code] ?? TONE.no_data
  return (
    <div className={`cq-verdict ${tone.cls}`} data-verdict={verdict.code}>
      <i className={tone.icon} aria-hidden="true"></i>
      <div>
        <div className="cq-verdict-text">{verdict.text}</div>
        <div className="cq-verdict-why">{verdict.why}</div>
        {(verdict.notes ?? []).map((n) => (
          <div key={n} className="cq-verdict-note">
            <i className="fas fa-info-circle" aria-hidden="true"></i> {n}
          </div>
        ))}
        {page.compare ? <CompareLine compare={page.compare} /> : null}
        <div className="cq-verdict-meta">
          Last {page.thresholds.window_minutes} minutes · target {page.settings.target} · every {page.settings.interval_s} s
          {page.lastSample ? ` · last measurement ${page.lastSample}` : ''}
        </div>
      </div>
    </div>
  )
}

/** Sectiunea Wi-Fi fara date: sta mereu pe ecran, ca omul sa stie ca exista. */
export function CqWifiNone() {
  return (
    <section className="cq-wifi" data-section="wifi" data-wifi="none">
      <div className="cq-wifi-head">
        <h5>
          <i className="fas fa-wifi" aria-hidden="true"></i> Wi-Fi
        </h5>
      </div>
      <p className="cq-muted cq-wifi-none">
        No Wi-Fi data for this device - it is on cable, Connection Quality is off, or its agent is older than 26.9.29.12
        (Wi-Fi readings came with that version). Data appears 5-10 minutes after it is switched on.
      </p>
    </section>
  )
}

function Enabled({ page, big, onOpenSettings }: { page: CqPage; big: boolean; onOpenSettings?: () => void }) {
  const verdict = page.verdict
  const { latency, loss, mos, http, tcp, common, httpCommon, markers, httpErrors } = cqSeries(page)
  const h = big ? { main: 360, small: 280 } : { main: 240, small: 200 }

  return (
    <>
      <CqVerdictBox page={page} />
      <CqChecksCategory checks={page.checks} />

      <div className="cq-lines">
        {LINES.map((l) => (
          <LineCard
            key={l.key}
            name={l.name}
            color={l.color}
            line={verdict.lines[l.key]}
            baseline={l.key === 'target' ? page.baseline.target : l.key === 'gateway' ? page.baseline.gateway : null}
          />
        ))}
      </div>

      <CqChart title="Latency (average, with min - max band)" unit="ms" series={latency} markers={markers} height={h.main} {...common} />
      <CqChart title="Packet loss" unit="%" series={loss} markers={markers} height={h.main} {...common} />
      {page.gatewaySilent ? (
        <p className="cq-note">
          Router does not answer ping (normal on some routers) - it is left out of the loss chart; traffic goes through it.
        </p>
      ) : null}
      <div className="cq-two">
        <CqChart title="Call quality to the target (MOS)" unit="MOS" series={mos} yMin={1} yMax={5} height={h.small} {...common} />
        <CqChart
          title="TCP retransmissions"
          unit="%"
          series={tcp}
          minTop={0.5}
          height={h.small}
          format={tcpFormat}
          {...common}
        />
      </div>
      {page.wifi ? (
        <>
          <CqWifi wifi={page.wifi} from={page.from} to={page.to} bucketS={page.bucket_s} onOpenSettings={onOpenSettings} tall={big} />
          <CqAccessPoints aps={page.wifi.aps ?? []} />
        </>
      ) : (
        <CqWifiNone />
      )}
      <DeviceFloorPosition id={page.device.id} currentPrefixes={page.wifi?.aps?.find((a) => a.current)?.prefixes ?? []} />
      <CqChart title="HTTPS request to the target" unit="ms" series={http} height={h.main} {...httpCommon} />
      {httpErrors > 0 ? (
        <p className="cq-note">
          {httpErrors === 1 ? '1 request' : `${httpErrors} requests`} to the target failed in this period.
        </p>
      ) : null}
    </>
  )
}

function LineCard({
  name,
  color,
  line,
  baseline,
}: {
  name: string
  color: string
  line: CqVerdict['lines']['gateway']
  baseline: CqBaseline | null
}) {
  const judged = line && line.sent > 0
  return (
    <div className={`cq-line${line?.bad ? ' is-bad' : ''}`} data-line={name}>
      <div className="cq-line-name">
        <span className="cq-swatch" style={{ background: color }}></span>
        {name}
        {line?.bad ? <span className="cq-flag">problem</span> : null}
      </div>
      {line?.silent ? (
        <div className="cq-line-values cq-muted">does not answer ping (normal on some routers)</div>
      ) : judged ? (
        <div className="cq-line-values">
          <span>
            <b>{line.rtt_avg ?? '-'}</b> ms
          </span>
          <span>
            <b>{line.loss_pct ?? 0}</b>% loss
          </span>
          {line.jitter !== null ? (
            <span>
              <b>{line.jitter}</b> ms jitter
            </span>
          ) : null}
        </div>
      ) : (
        <div className="cq-line-values cq-muted">not measured</div>
      )}
      {baseline ? <Normal baseline={baseline} /> : null}
    </div>
  )
}

function Normal({ baseline }: { baseline: CqBaseline }) {
  if (!baseline.ready) {
    return <div className="cq-normal cq-muted">Normal for this device: not enough history yet</div>
  }
  return (
    <div className={`cq-normal${baseline.above ? ' is-above' : ''}`}>
      Normal for this device: {baseline.median} ms (7-day median)
      {baseline.above ? (
        <strong className="cq-above">
          {' '}
          · now {baseline.current} ms, above this device's normal
        </strong>
      ) : null}
    </div>
  )
}
